/**
 * RecentProjects — quiet list of active projects on the dashboard.
 *
 * Replaces the prior shadcn-default Card+Header treatment with a
 * self-anchored section using `.section-heading` / `.section-eyebrow`
 * for visual parity with the rest of the dashboard. Each project
 * row is a distinct horizontally-arranged card with a status pill
 * on the right — readable at a glance, no busy decoration.
 */
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, FolderOpen } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useProjects } from '@/services/projects';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { relativeOrbit } from '@/lib/relativeTime';
import { asDay } from '@/lib/dates';

// Status color mapping — uses orbit status tokens where available
// and falls back to color stops for the project lifecycle stages
// that don't have dedicated tokens yet (editing, etc.).
//
// BRAND-1 — `review` was orange-500 and is now Kapton Gold: the
// project is parked waiting on someone, which is exactly the
// pending / follow-up role gold carries everywhere else (see
// --status-pending in index.css). The remaining stops are stock
// Tailwind and outside this wave — they were never orange.
const statusColors: Record<string, string> = {
  discovery:      'bg-blue-500/10 text-blue-500 border-blue-500/20',
  pre_production: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  production:     'bg-green-500/10 text-green-600 border-green-500/20',
  editing:        'bg-purple-500/10 text-purple-500 border-purple-500/20',
  review:         'bg-orbit-gold-fill/10 text-orbit-gold border-orbit-gold/20',
  delivered:      'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
};

const formatStatus = (status: string) => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const RecentProjects = () => {
  const { data: projects = [], isLoading } = useProjects();

  // Show top 5 active projects (not delivered/archived).
  const recentProjects = projects.filter((p) => p.status !== 'delivered').slice(0, 5);

  // Hide entirely when the studio has zero projects — the empty
  // state belongs upstream on the inquiry/empty splash, not here.
  if (!isLoading && recentProjects.length === 0 && projects.length === 0) {
    return null;
  }

  return (
    <section className="space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <p className="section-eyebrow flex items-center gap-1.5">
            <OrbitIcon name="folder" fallback={FolderOpen} className="w-3 h-3" />
            Production
          </p>
          <h2 className="section-heading text-xl md:text-2xl text-foreground">Active projects</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? 'Loading…'
              : recentProjects.length > 0
              ? `${recentProjects.length} active project${recentProjects.length !== 1 ? 's' : ''}`
              : 'All projects delivered'}
          </p>
        </div>
        <Button variant="ghost" size="sm" asChild className="gap-1 text-muted-foreground hover:text-foreground">
          <Link to="/projects">
            View all
            <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4" />
          </Link>
        </Button>
      </header>

      {recentProjects.length > 0 ? (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          {recentProjects.map((project, idx) => (
            <Link
              key={project.id}
              to={`/studio/${project.id}`}
              className={cn(
                'flex items-center justify-between gap-4 px-5 py-4 transition-colors group',
                'hover:bg-primary/[0.03]',
                idx > 0 && 'border-t border-border',
              )}
            >
              <div className="flex flex-col gap-1 min-w-0">
                <span className="font-display text-base font-semibold text-foreground truncate tracking-tight group-hover:text-primary transition-colors">
                  {project.name}
                </span>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {'client' in project && project.client && (
                    <span className="truncate">
                      {(project.client as { name: string }).name}
                    </span>
                  )}
                  {'due_date' in project && project.due_date && (
                    <>
                      <span className="flex items-center gap-1">
                        <OrbitIcon name="calendar" fallback={Calendar} className="w-3 h-3" />
                        {format(asDay(project.due_date), 'MMM d')}
                      </span>
                      {/* Anticipatory countdown — "in 12 days" / "tomorrow".
                          Uses lunar-teal accent so it reads as warm context,
                          not another data point. */}
                      <span className="text-[hsl(var(--accent))] font-medium">
                        {relativeOrbit(project.due_date)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Badge
                variant="outline"
                className={cn(
                  'shrink-0 capitalize text-xs px-2.5 py-1 font-medium',
                  statusColors[project.status] || '',
                )}
              >
                {formatStatus(project.status)}
              </Badge>
            </Link>
          ))}
        </div>
      ) : !isLoading ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center">
          <p className="text-foreground font-medium">All projects delivered.</p>
          <p className="text-sm text-muted-foreground mt-1">Time to celebrate — or to find your next client.</p>
          <Button variant="outline" size="sm" asChild className="mt-4">
            <Link to="/projects">View all projects</Link>
          </Button>
        </div>
      ) : null}
    </section>
  );
};

export default RecentProjects;
